import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../../auth/AuthContext";

import {
  verifyCurrentPassword,
} from "../../auth/auth.service";

import SecureConfirm from "../../components/SecureConfirm/SecureConfirm";

import {
  logAuditEvent,
} from "../../core/audit.service";

import {
  alterarStatusProduto,
  ajustarEstoque,
  criarProduto,
  editarProduto,
  loadEstoque,
  loadMovimentosEstoque,
  registrarEntrada,
} from "./estoque.service";

import type {
  EstoqueProduto,
  MovimentoEstoque,
} from "./types";

import "./Estoque.css";

type ModalMode =
  | null
  | "novo"
  | "editar"
  | "entrada"
  | "ajuste"
  | "historico";

function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(value);
}

function formatDateTime(
  value: string,
) {
  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      dateStyle: "short",
      timeStyle: "short",
    },
  ).format(
    new Date(value),
  );
}

function getErrorMessage(
  error: unknown,
) {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error
  ) {
    return String(
      (
        error as {
          message?: string;
        }
      ).message ||
        "Não foi possível realizar a operação.",
    );
  }

  return "Não foi possível realizar a operação.";
}

export default function Estoque() {
  const {
    profile,
    session,
    can,
  } = useAuth();

  const [
    produtos,
    setProdutos,
  ] =
    useState<EstoqueProduto[]>([]);

  const [
    selectedProduct,
    setSelectedProduct,
  ] =
    useState<EstoqueProduto | null>(
      null,
    );

  const [
    movimentos,
    setMovimentos,
  ] =
    useState<MovimentoEstoque[]>([]);

  const [
    modalMode,
    setModalMode,
  ] =
    useState<ModalMode>(
      null,
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    historyLoading,
    setHistoryLoading,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [stockTab, setStockTab] =
    useState<"ativos" | "desativados">("ativos");

  const [
    nome,
    setNome,
  ] =
    useState("");

  const [
    unidade,
    setUnidade,
  ] =
    useState("un");

  const [
    quantidadeInicial,
    setQuantidadeInicial,
  ] =
    useState(0);

  const [
    estoqueMinimo,
    setEstoqueMinimo,
  ] =
    useState(0);

  const [
    valor,
    setValor,
  ] =
    useState(0);

  const [
    quantidadeEntrada,
    setQuantidadeEntrada,
  ] =
    useState(1);

  const [
    motivoEntrada,
    setMotivoEntrada,
  ] =
    useState("");

  const [
    novaQuantidade,
    setNovaQuantidade,
  ] =
    useState(0);

  const [
    motivoAjuste,
    setMotivoAjuste,
  ] =
    useState("");

  const [
    confirmAdjustOpen,
    setConfirmAdjustOpen,
  ] =
    useState(false);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        const result =
          await loadEstoque();

        setProdutos(result);

        setError("");
      } catch (err) {
        console.error(err);

        setError(
          "Não foi possível carregar o estoque.",
        );
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const activeProducts = useMemo(
    () => produtos.filter((produto) => produto.ativo !== false),
    [produtos],
  );

  const inactiveProducts = useMemo(
    () => produtos.filter((produto) => produto.ativo === false),
    [produtos],
  );

  const filteredProducts =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      const source = stockTab === "ativos"
        ? activeProducts
        : inactiveProducts;

      if (!term) {
        return source;
      }

      return source.filter(
        (produto) =>
          produto.nome
            .toLowerCase()
            .includes(term),
      );
    }, [
      activeProducts,
      inactiveProducts,
      search,
      stockTab,
    ]);

  const lowStockCount =
    useMemo(
      () =>
        activeProducts.filter(
          (produto) =>
            Number(
              produto.quantidade,
            ) <=
            Number(
              produto.estoque_minimo,
            ),
        ).length,
      [activeProducts],
    );

  const totalUnits =
    useMemo(
      () =>
        activeProducts.reduce(
          (
            total,
            produto,
          ) =>
            total +
            Number(
              produto.quantidade,
            ),
          0,
        ),
      [activeProducts],
    );

  const inventoryValue =
    useMemo(
      () =>
        activeProducts.reduce(
          (
            total,
            produto,
          ) =>
            total +
            Number(
              produto.quantidade,
            ) *
              Number(
                produto.valor,
              ),
          0,
        ),
      [activeProducts],
    );

  function updateProduct(
    updated: EstoqueProduto,
  ) {
    setProdutos(
      (current) =>
        current
          .map(
            (produto) =>
              produto.id ===
              updated.id
                ? updated
                : produto,
          )
          .sort(
            (a, b) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
    );

    setSelectedProduct(
      updated,
    );
  }

  function closeModal() {
    if (saving) {
      return;
    }

    setModalMode(null);

    setSelectedProduct(
      null,
    );

    setError("");

    setConfirmAdjustOpen(
      false,
    );
  }

  function openNewProduct() {
    setSelectedProduct(
      null,
    );

    setNome("");
    setUnidade("un");

    setQuantidadeInicial(
      0,
    );

    setEstoqueMinimo(
      0,
    );

    setValor(0);

    setError("");

    setModalMode(
      "novo",
    );
  }

  function openEdit(
    produto: EstoqueProduto,
  ) {
    setSelectedProduct(
      produto,
    );

    setNome(
      produto.nome,
    );

    setUnidade(
      produto.unidade,
    );

    setEstoqueMinimo(
      Number(
        produto.estoque_minimo,
      ),
    );

    setValor(
      Number(
        produto.valor,
      ),
    );

    setError("");

    setModalMode(
      "editar",
    );
  }

  function openEntry(
    produto: EstoqueProduto,
  ) {
    setSelectedProduct(
      produto,
    );

    setQuantidadeEntrada(
      1,
    );

    setMotivoEntrada(
      "",
    );

    setError("");

    setModalMode(
      "entrada",
    );
  }

  function openAdjustment(
    produto: EstoqueProduto,
  ) {
    setSelectedProduct(
      produto,
    );

    setNovaQuantidade(
      Number(
        produto.quantidade,
      ),
    );

    setMotivoAjuste(
      "",
    );

    setError("");

    setModalMode(
      "ajuste",
    );
  }

  async function openHistory(
    produto: EstoqueProduto,
  ) {
    setSelectedProduct(
      produto,
    );

    setModalMode(
      "historico",
    );

    setMovimentos([]);

    try {
      setHistoryLoading(
        true,
      );

      const result =
        await loadMovimentosEstoque(
          produto.id,
        );

      setMovimentos(
        result,
      );
    } catch (err) {
      console.error(err);

      setError(
        "Não foi possível carregar o histórico.",
      );
    } finally {
      setHistoryLoading(
        false,
      );
    }
  }

  async function handleCreate() {
    if (!nome.trim()) {
      setError(
        "Informe o nome do produto.",
      );

      return;
    }

    if (!unidade.trim()) {
      setError(
        "Informe a unidade.",
      );

      return;
    }

    try {
      setSaving(true);
      setError("");

      const created =
        await criarProduto({
          nome:
            nome.trim(),

          unidade:
            unidade.trim(),

          quantidadeInicial:
            Number(
              quantidadeInicial,
            ),

          estoqueMinimo:
            Number(
              estoqueMinimo,
            ),

          valor:
            Number(
              valor,
            ),

          createdBy:
            profile?.id ??
            null,
        });

      setProdutos(
        (current) =>
          [
            ...current,
            created,
          ].sort(
            (a, b) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "PRODUTO_ESTOQUE_CRIADO",

          level:
            "success",

          module:
            "estoque",

          entityType:
            "estoque",

          entityId:
            created.id,

          description:
            `${profile?.nome || "Funcionário"} cadastrou o produto ${created.nome}.`,

          metadata: {
            produto:
              created.nome,

            quantidade_inicial:
              created.quantidade,

            estoque_minimo:
              created.estoque_minimo,

            valor:
              created.valor,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      closeModal();
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit() {
    if (
      !selectedProduct
    ) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const updated =
        await editarProduto({
          id:
            selectedProduct.id,

          nome:
            nome.trim(),

          unidade:
            unidade.trim(),

          estoqueMinimo:
            Number(
              estoqueMinimo,
            ),

          valor:
            Number(
              valor,
            ),
        });

      updateProduct(
        updated,
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "PRODUTO_ESTOQUE_EDITADO",

          level:
            "info",

          module:
            "estoque",

          entityType:
            "estoque",

          entityId:
            updated.id,

          description:
            `${profile?.nome || "Funcionário"} editou o produto ${updated.nome}.`,

          metadata: {
            antes: {
              nome:
                selectedProduct.nome,

              unidade:
                selectedProduct.unidade,

              estoque_minimo:
                selectedProduct.estoque_minimo,

              valor:
                selectedProduct.valor,
            },

            depois: {
              nome:
                updated.nome,

              unidade:
                updated.unidade,

              estoque_minimo:
                updated.estoque_minimo,

              valor:
                updated.valor,
            },
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      closeModal();
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleEntry() {
    if (
      !selectedProduct
    ) {
      return;
    }

    if (
      Number(
        quantidadeEntrada,
      ) <= 0
    ) {
      setError(
        "Informe uma quantidade maior que zero.",
      );

      return;
    }

    try {
      setSaving(true);
      setError("");

      const result =
        await registrarEntrada(
          selectedProduct.id,

          Number(
            quantidadeEntrada,
          ),

          motivoEntrada.trim(),

          profile?.id ??
            null,
        );

      updateProduct(
        result.produto,
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ESTOQUE_ENTRADA",

          level:
            "success",

          module:
            "estoque",

          entityType:
            "estoque",

          entityId:
            selectedProduct.id,

          description:
            `${profile?.nome || "Funcionário"} registrou entrada de ${quantidadeEntrada} ${selectedProduct.unidade} em ${selectedProduct.nome}.`,

          metadata: {
            produto:
              selectedProduct.nome,

            quantidade:
              Number(
                quantidadeEntrada,
              ),

            antes:
              result.quantidade_antes,

            depois:
              result.quantidade_depois,

            motivo:
              motivoEntrada.trim() ||
              null,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      closeModal();
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  function requestAdjustment() {
    if (
      !selectedProduct
    ) {
      return;
    }

    if (
      !motivoAjuste.trim()
    ) {
      setError(
        "Informe o motivo do ajuste.",
      );

      return;
    }

    if (
      Number(
        novaQuantidade,
      ) < 0
    ) {
      setError(
        "A quantidade não pode ser negativa.",
      );

      return;
    }

    setConfirmAdjustOpen(
      true,
    );
  }

  async function confirmAdjustment(
    password: string,
  ) {
    if (
      !selectedProduct ||
      !session?.user.email
    ) {
      throw new Error(
        "Não foi possível identificar o usuário atual.",
      );
    }

    try {
      await verifyCurrentPassword(
        session.user.email,
        password,
      );
    } catch {
      try {
        await logAuditEvent({
          userId:
            session.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ESTOQUE_AJUSTE_NEGADO",

          level:
            "security",

          module:
            "estoque",

          entityType:
            "estoque",

          entityId:
            selectedProduct.id,

          description:
            `${profile?.nome || "Funcionário"} tentou ajustar ${selectedProduct.nome}, mas a confirmação de senha falhou.`,

          metadata: {
            produto:
              selectedProduct.nome,

            quantidade_atual:
              selectedProduct.quantidade,

            quantidade_solicitada:
              Number(
                novaQuantidade,
              ),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      throw new Error(
        "Senha incorreta.",
      );
    }

    try {
      setSaving(true);

      const result =
        await ajustarEstoque(
          selectedProduct.id,

          Number(
            novaQuantidade,
          ),

          motivoAjuste.trim(),

          profile?.id ??
            null,
        );

      updateProduct(
        result.produto,
      );

      try {
        await logAuditEvent({
          userId:
            session.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ESTOQUE_AJUSTADO",

          level:
            "warning",

          module:
            "estoque",

          entityType:
            "estoque",

          entityId:
            selectedProduct.id,

          description:
            `${profile?.nome || "Funcionário"} ajustou manualmente o estoque de ${selectedProduct.nome}.`,

          metadata: {
            produto:
              selectedProduct.nome,

            antes:
              result.quantidade_antes,

            depois:
              result.quantidade_depois,

            diferenca:
              result.diferenca,

            motivo:
              motivoAjuste.trim(),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      setConfirmAdjustOpen(
        false,
      );

      closeModal();
    } catch (err) {
      console.error(err);

      throw new Error(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleProductStatus(
    produto: EstoqueProduto,
    ativo: boolean,
  ) {
    const action = ativo ? "reativar" : "desativar";
    const confirmed = window.confirm(
      ativo
        ? `Deseja reativar ${produto.nome}? Ele voltará a aparecer nas comandas.`
        : `Deseja desativar ${produto.nome}? Ele sairá das telas de venda, mas o histórico será preservado.`,
    );

    if (!confirmed) return;

    try {
      setSaving(true);
      setError("");

      const updated = await alterarStatusProduto(
        produto.id,
        ativo,
      );

      updateProduct(updated);

      try {
        await logAuditEvent({
          userId: session?.user.id,
          employeeId: profile?.id,
          employeeName: profile?.nome,
          employeeLoginId: profile?.login_id,
          eventType: ativo
            ? "ESTOQUE_PRODUTO_REATIVADO"
            : "ESTOQUE_PRODUTO_DESATIVADO",
          level: ativo ? "success" : "warning",
          module: "estoque",
          entityType: "estoque",
          entityId: produto.id,
          description: `${profile?.nome || "Funcionário"} decidiu ${action} o produto ${produto.nome}.`,
          metadata: {
            produto: produto.nome,
            ativo,
            quantidade: produto.quantidade,
          },
        });
      } catch (auditError) {
        console.error("Falha na auditoria:", auditError);
      }
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (
    !can(
      "estoque.view",
    )
  ) {
    return (
      <section className="estoque-page">
        <div className="estoque-empty">
          Você não possui acesso ao estoque.
        </div>
      </section>
    );
  }

  return (
    <section className="estoque-page">
      <div className="estoque-header">
        <div>
          <span className="estoque-eyebrow">
            OPERAÇÃO
          </span>

          <h2>
            Estoque
          </h2>

          <p>
            Controle produtos, quantidades e reposições.
          </p>
        </div>

        {can(
          "estoque.edit",
        ) && (
          <button
            type="button"
            className="estoque-new"
            onClick={
              openNewProduct
            }
          >
            + Novo produto
          </button>
        )}
      </div>

      <div className="estoque-summary">
        <article>
          <span>
            Produtos
          </span>

          <strong>
            {
              activeProducts.length
            }
          </strong>
        </article>

        <article>
          <span>
            Unidades
          </span>

          <strong>
            {
              totalUnits
            }
          </strong>
        </article>

        <article
          className={
            lowStockCount > 0
              ? "warning"
              : ""
          }
        >
          <span>
            Estoque baixo
          </span>

          <strong>
            {
              lowStockCount
            }
          </strong>
        </article>

        <article>
          <span>
            Valor em venda
          </span>

          <strong>
            {formatBRL(
              inventoryValue,
            )}
          </strong>
        </article>
      </div>

      <div className="estoque-tabs">
        <button
          type="button"
          className={stockTab === "ativos" ? "active" : ""}
          onClick={() => setStockTab("ativos")}
        >
          Ativos ({activeProducts.length})
        </button>
        <button
          type="button"
          className={stockTab === "desativados" ? "active" : ""}
          onClick={() => setStockTab("desativados")}
        >
          Desativados ({inactiveProducts.length})
        </button>
      </div>

      <div className="estoque-toolbar">
        <input
          type="search"
          placeholder="Buscar produto..."
          value={search}
          onChange={(
            event,
          ) =>
            setSearch(
              event.target.value,
            )
          }
        />

        <span>
          {
            filteredProducts.length
          }{" "}
          produtos
        </span>
      </div>

      {error &&
        !modalMode && (
          <div className="estoque-error">
            {error}
          </div>
        )}

      {loading ? (
        <div className="estoque-empty">
          Carregando estoque...
        </div>
      ) : filteredProducts.length ===
        0 ? (
        <div className="estoque-empty">
          Nenhum produto encontrado.
        </div>
      ) : (
        <div className="estoque-grid">
          {filteredProducts.map(
            (produto) => {
              const low =
                Number(
                  produto.quantidade,
                ) <=
                Number(
                  produto.estoque_minimo,
                );

              return (
                <article
                  key={
                    produto.id
                  }
                  className={`estoque-card ${
                    low
                      ? "low"
                      : ""
                  } ${produto.ativo === false ? "inactive" : ""}`}
                >
                  <div className="estoque-card-top">
                    <div>
                      <span className="estoque-status">
                        {produto.ativo === false
                          ? "DESATIVADO"
                          : low
                          ? "ESTOQUE BAIXO"
                          : "DISPONÍVEL"}
                      </span>

                      <h3>
                        {
                          produto.nome
                        }
                      </h3>
                    </div>

                    <strong className="estoque-price">
                      {formatBRL(
                        Number(
                          produto.valor,
                        ),
                      )}
                    </strong>
                  </div>

                  <div className="estoque-quantity">
                    <strong>
                      {
                        produto.quantidade
                      }
                    </strong>

                    <span>
                      {
                        produto.unidade
                      }
                    </span>
                  </div>

                  <div className="estoque-minimum">
                    Mínimo:{" "}
                    {
                      produto.estoque_minimo
                    }{" "}
                    {
                      produto.unidade
                    }
                  </div>

                  <div className="estoque-actions">
                    {can(
                      "estoque.edit",
                    ) && produto.ativo !== false && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            openEntry(
                              produto,
                            )
                          }
                        >
                          + Entrada
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            openEdit(
                              produto,
                            )
                          }
                        >
                          Editar
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            openAdjustment(
                              produto,
                            )
                          }
                        >
                          Ajustar
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        openHistory(
                          produto,
                        )
                      }
                    >
                      Histórico
                    </button>

                    {can("estoque.edit") && (
                      <button
                        type="button"
                        className={produto.ativo === false ? "reactivate" : "deactivate"}
                        onClick={() => handleProductStatus(
                          produto,
                          produto.ativo === false,
                        )}
                        disabled={saving}
                      >
                        {produto.ativo === false ? "Reativar" : "Desativar"}
                      </button>
                    )}
                  </div>
                </article>
              );
            },
          )}
        </div>
      )}

      {modalMode && (
        <div
          className="estoque-modal-backdrop"
          onClick={
            closeModal
          }
        >
          <div
            className="estoque-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="estoque-modal-header">
              <div>
                <span className="estoque-eyebrow">
                  {modalMode ===
                  "novo"
                    ? "CADASTRO"
                    : modalMode ===
                        "editar"
                      ? "EDIÇÃO"
                      : modalMode ===
                          "entrada"
                        ? "REPOSIÇÃO"
                        : modalMode ===
                            "ajuste"
                          ? "AJUSTE MANUAL"
                          : "HISTÓRICO"}
                </span>

                <h3>
                  {modalMode ===
                  "novo"
                    ? "Novo produto"
                    : selectedProduct
                      ?.nome}
                </h3>
              </div>

              <button
                type="button"
                className="estoque-modal-close"
                onClick={
                  closeModal
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>

            {(modalMode ===
              "novo" ||
              modalMode ===
                "editar") && (
              <>
                <label className="estoque-field">
                  Nome

                  <input
                    autoFocus
                    value={
                      nome
                    }
                    onChange={(
                      event,
                    ) =>
                      setNome(
                        event
                          .target
                          .value,
                      )
                    }
                  />
                </label>

                <label className="estoque-field">
                  Unidade

                  <input
                    value={
                      unidade
                    }
                    onChange={(
                      event,
                    ) =>
                      setUnidade(
                        event
                          .target
                          .value,
                      )
                    }
                    placeholder="un"
                  />
                </label>

                {modalMode ===
                  "novo" && (
                  <label className="estoque-field">
                    Quantidade inicial

                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={
                        quantidadeInicial
                      }
                      onChange={(
                        event,
                      ) =>
                        setQuantidadeInicial(
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                    />
                  </label>
                )}

                <label className="estoque-field">
                  Estoque mínimo

                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={
                      estoqueMinimo
                    }
                    onChange={(
                      event,
                    ) =>
                      setEstoqueMinimo(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                    }
                  />
                </label>

                <label className="estoque-field">
                  Preço de venda

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      valor
                    }
                    onChange={(
                      event,
                    ) =>
                      setValor(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                    }
                  />
                </label>
              </>
            )}

            {modalMode ===
              "entrada" &&
              selectedProduct && (
                <>
                  <div className="estoque-current">
                    <span>
                      Estoque atual
                    </span>

                    <strong>
                      {
                        selectedProduct.quantidade
                      }{" "}
                      {
                        selectedProduct.unidade
                      }
                    </strong>
                  </div>

                  <label className="estoque-field">
                    Quantidade recebida

                    <input
                      autoFocus
                      type="number"
                      min="0.01"
                      step="1"
                      value={
                        quantidadeEntrada
                      }
                      onChange={(
                        event,
                      ) =>
                        setQuantidadeEntrada(
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                    />
                  </label>

                  <label className="estoque-field">
                    Observação

                    <input
                      value={
                        motivoEntrada
                      }
                      onChange={(
                        event,
                      ) =>
                        setMotivoEntrada(
                          event
                            .target
                            .value,
                        )
                      }
                      placeholder="Ex.: Reposição semanal"
                    />
                  </label>

                  <div className="estoque-preview success">
                    Novo estoque:{" "}
                    <strong>
                      {Number(
                        selectedProduct.quantidade,
                      ) +
                        Number(
                          quantidadeEntrada,
                        )}{" "}
                      {
                        selectedProduct.unidade
                      }
                    </strong>
                  </div>
                </>
              )}

            {modalMode ===
              "ajuste" &&
              selectedProduct && (
                <>
                  <div className="estoque-adjust-warning">
                    Use o ajuste somente quando a quantidade física for diferente do sistema.
                  </div>

                  <div className="estoque-current">
                    <span>
                      Estoque no sistema
                    </span>

                    <strong>
                      {
                        selectedProduct.quantidade
                      }{" "}
                      {
                        selectedProduct.unidade
                      }
                    </strong>
                  </div>

                  <label className="estoque-field">
                    Quantidade física correta

                    <input
                      autoFocus
                      type="number"
                      min="0"
                      step="1"
                      value={
                        novaQuantidade
                      }
                      onChange={(
                        event,
                      ) =>
                        setNovaQuantidade(
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                    />
                  </label>

                  <label className="estoque-field">
                    Motivo do ajuste

                    <textarea
                      value={
                        motivoAjuste
                      }
                      onChange={(
                        event,
                      ) =>
                        setMotivoAjuste(
                          event
                            .target
                            .value,
                        )
                      }
                      placeholder="Ex.: Contagem física encontrou 2 unidades a menos."
                    />
                  </label>
                </>
              )}

            {modalMode ===
              "historico" &&
              selectedProduct && (
                <div className="estoque-history">
                  {historyLoading ? (
                    <div className="estoque-empty">
                      Carregando histórico...
                    </div>
                  ) : movimentos.length ===
                    0 ? (
                    <div className="estoque-empty">
                      Nenhuma movimentação registrada.
                    </div>
                  ) : (
                    movimentos.map(
                      (
                        movimento,
                      ) => (
                        <div
                          key={
                            movimento.id
                          }
                          className="estoque-history-item"
                        >
                          <div>
                            <strong>
                              {movimento.tipo.toUpperCase()}
                            </strong>

                            <span>
                              {formatDateTime(
                                movimento.created_at,
                              )}
                            </span>
                          </div>

                          <div className="estoque-history-values">
                            <strong>
                              {
                                movimento.quantidade_antes
                              }{" "}
                              →{" "}
                              {
                                movimento.quantidade_depois
                              }
                            </strong>

                            <span>
                              {Number(
                                movimento.quantidade,
                              ) > 0
                                ? "+"
                                : ""}
                              {
                                movimento.quantidade
                              }{" "}
                              {
                                selectedProduct.unidade
                              }
                            </span>
                          </div>

                          <p>
                            {movimento.motivo ||
                              movimento.origem ||
                              "Sem observação"}
                          </p>
                        </div>
                      ),
                    )
                  )}
                </div>
              )}

            {error && (
              <div className="estoque-error">
                {error}
              </div>
            )}

            {modalMode !==
              "historico" && (
              <div className="estoque-modal-actions">
                <button
                  type="button"
                  className="estoque-cancel"
                  onClick={
                    closeModal
                  }
                  disabled={
                    saving
                  }
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className={
                    modalMode ===
                    "ajuste"
                      ? "estoque-danger"
                      : "estoque-confirm"
                  }
                  onClick={
                    modalMode ===
                    "novo"
                      ? handleCreate
                      : modalMode ===
                          "editar"
                        ? handleEdit
                        : modalMode ===
                            "entrada"
                          ? handleEntry
                          : requestAdjustment
                  }
                  disabled={
                    saving
                  }
                >
                  {saving
                    ? "Salvando..."
                    : modalMode ===
                        "novo"
                      ? "Cadastrar produto"
                      : modalMode ===
                          "editar"
                        ? "Salvar alterações"
                        : modalMode ===
                            "entrada"
                          ? "Registrar entrada"
                          : "Confirmar ajuste"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <SecureConfirm
        open={
          confirmAdjustOpen
        }
        title="Confirmar ajuste de estoque"
        message={
          selectedProduct
            ? `Você está alterando ${selectedProduct.nome} de ${selectedProduct.quantidade} para ${novaQuantidade} ${selectedProduct.unidade}. Digite sua senha para confirmar.`
            : ""
        }
        confirmLabel="Confirmar ajuste"
        danger
        loading={
          saving
        }
        onCancel={() =>
          setConfirmAdjustOpen(
            false,
          )
        }
        onConfirm={
          confirmAdjustment
        }
      />
    </section>
  );
}
